// =============================================================================
// WarbachFeedbackWidget — Floating feedback button + slide-up panel.
//
// Warbach is a kobold scout who has been tasked with collecting feedback
// from adventurers passing through Darkfeld. He appears as a small avatar
// in the bottom-left corner of every page.
//
// 🐉 Easter Egg — if the submitted feedback mentions zombies, undead, or
// related creatures, Warbach emits a terrified scream and flees off the
// right edge of the screen. He will not return until the page is reloaded
// or the user navigates away (component unmounts).
// =============================================================================
import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Send, Loader2, CheckCircle2, Bug, Sparkles, MessageSquare } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAppSelector } from '@/store/hooks';
import WarbachAvatar from './WarbachAvatar';

type FeedbackType = 'bug' | 'feature' | 'general';

/**
 * Warbach's emotional state — drives animation and render.
 * idle → screaming → panicking → fleeing → fled
 */
type WarbachMood = 'idle' | 'screaming' | 'panicking' | 'fleeing' | 'fled';

interface FeedbackTypeOption {
  value: FeedbackType;
  label: string;
  description: string;
  Icon: React.ElementType;
  activeClass: string;
}

const FEEDBACK_TYPES: FeedbackTypeOption[] = [
  {
    value: 'bug',
    label: 'Bug Report',
    description: 'Something is broken or behaving oddly',
    Icon: Bug,
    activeClass: 'border-danger/50 bg-danger/10 text-danger',
  },
  {
    value: 'feature',
    label: 'Feature Request',
    description: 'An idea for something new or improved',
    Icon: Sparkles,
    activeClass: 'border-info/50 bg-info/10 text-info',
  },
  {
    value: 'general',
    label: 'General',
    description: 'Anything else you want to share',
    Icon: MessageSquare,
    activeClass: 'border-gold/50 bg-gold/10 text-gold',
  },
];

/** Warbach's rotating opening lines — flavour text shown in the panel header. */
const WARBACH_LINES = [
  "Warbach here! Got a moment to help improve Darkfeld?",
  "Psst! Warbach needs your eyes — spotted anything odd?",
  "Hss! Your feedback feeds Warbach's lantern!",
  "Warbach has been watching. Tell him what could be better.",
  "Even a kobold knows a good idea when he hears one!",
  "You'd better hurry — the Zombie Apocalypse is coming and Warbach still needs your feedback!",
  "Warbach scribbled seventeen things in his scouting journal today. Add yours!",
  "A kobold never wastes a torch — or a bug report. What have you found?",
  "The lantern dims without purpose… give Warbach something to write!",
  "Speak now! Good ideas are rarer than dragon eggs around here.",
  "Warbach's quill is sharpened and ready. What troubles you, adventurer?",
  "Two copper pieces for your thoughts — Warbach accepts all currencies.",
];

// ---------------------------------------------------------------------------
// Easter egg — undead / zombie keyword detector
// ---------------------------------------------------------------------------
const UNDEAD_KEYWORDS: string[] = [
  'zombie', 'zombies', 'undead', 'un-dead',
  'lich', 'liches', 'wight', 'wights',
  'ghoul', 'ghouls', 'ghast', 'ghasts',
  'vampire', 'vampires', 'skeleton', 'skeletons',
  'necromancer', 'necromancers', 'necromancy',
  'wraith', 'wraiths', 'specter', 'specters', 'spectre', 'spectres',
  'revenant', 'revenants', 'death knight', 'death knights',
  'banshee', 'banshees', 'draugr', 'draugrs',
  'mummy', 'mummies', 'dullahan', 'poltergeist',
  'risen dead', 'the dead walk', 'dead walk', 'undying',
  'night creature', 'night creatures',
];

function mentionsUndead(text: string): boolean {
  const lower = text.toLowerCase();
  return UNDEAD_KEYWORDS.some((kw) => lower.includes(kw));
}

// Easter-egg timing: scream (1.5 s) → panic run (5.0 s) → flee off-screen (1.5 s) → null
// Total visible animation: 8 seconds before Warbach disappears for the session.
const SCREAM_MS = 1500;
const PANIC_MS  = 5000;
const FLEE_MS   = 1500;

export default function WarbachFeedbackWidget() {
  const { user } = useAppSelector((s) => s.auth);
  const location = useLocation();

  const [open,         setOpen]        = useState(false);
  const [type,         setType]        = useState<FeedbackType>('general');
  const [description,  setDescription] = useState('');
  const [submitting,   setSubmitting]  = useState(false);
  const [submitted,    setSubmitted]   = useState(false);
  const [error,        setError]       = useState<string | null>(null);
  const [warbachMood,  setWarbachMood] = useState<WarbachMood>('idle');
  const [warbachLine, setWarbachLine]  = useState(
    () => WARBACH_LINES[Math.floor(Math.random() * WARBACH_LINES.length)]
  );

  const textareaRef  = useRef<HTMLTextAreaElement>(null);
  const panelRef     = useRef<HTMLDivElement>(null);
  const buttonRef    = useRef<HTMLButtonElement>(null);
  const fleeTimers   = useRef<ReturnType<typeof setTimeout>[]>([]);

  // ── Overlap detection ──────────────────────────────────────────────────────
  // Use elementsFromPoint to check whether the floating button is sitting on
  // top of interactive page content (links, buttons, inputs). When it is, the
  // label text is hidden so the footprint shrinks to just the avatar pill,
  // clearing access to whatever is underneath.
  const [isObscuring, setIsObscuring] = useState(false);

  const INTERACTIVE_TAGS = new Set(['a', 'button', 'input', 'select', 'textarea', 'label']);

  const checkOverlap = useCallback(() => {
    const btn = buttonRef.current;
    // Clear the flag during any animation phase — Warbach is mid-sequence and
    // will disappear shortly anyway, so overlap detection is moot.
    const animating = warbachMood === 'screaming' || warbachMood === 'panicking' || warbachMood === 'fleeing';
    if (!btn || animating) {
      setIsObscuring(false);
      return;
    }
    const rect = btn.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    // Sample the four inset corners of the button so we catch partial overlaps.
    const samplePoints: [number, number][] = [
      [rect.left   + 6, rect.top    + 6],
      [rect.right  - 6, rect.top    + 6],
      [rect.left   + 6, rect.bottom - 6],
      [rect.right  - 6, rect.bottom - 6],
    ];

    for (const [x, y] of samplePoints) {
      const stack = document.elementsFromPoint(x, y);
      // Walk the z-stack below our button and look for interactive elements.
      let pastUs = false;
      for (const el of stack) {
        if (!pastUs) {
          if (btn.contains(el) || el === btn) { pastUs = true; }
          continue;
        }
        const tag = el.tagName.toLowerCase();
        const role = el.getAttribute('role');
        if (
          INTERACTIVE_TAGS.has(tag) ||
          role === 'button' ||
          role === 'link' ||
          role === 'menuitem' ||
          (el as HTMLElement).tabIndex >= 0
        ) {
          setIsObscuring(true);
          return;
        }
      }
    }
    setIsObscuring(false);
  }, [warbachMood]);

  useEffect(() => {
    // Run immediately so the first render is correct, then re-run on any
    // layout shift caused by scroll or resize.
    checkOverlap();
    window.addEventListener('scroll',  checkOverlap, { passive: true });
    window.addEventListener('resize',  checkOverlap, { passive: true });
    return () => {
      window.removeEventListener('scroll',  checkOverlap);
      window.removeEventListener('resize',  checkOverlap);
    };
  }, [checkOverlap]);

  // Focus textarea when panel opens
  useEffect(() => {
    if (open && !submitted) {
      setTimeout(() => textareaRef.current?.focus(), 80);
    }
  }, [open, submitted]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  // Clear timers on unmount (navigation) so no setState on dead component
  useEffect(() => {
    return () => { fleeTimers.current.forEach(clearTimeout); };
  }, []);

  // ── Flee sequence ──────────────────────────────────────────────────────────
  /**
   * Trigger Warbach's terrified flee animation.
   * screaming → panicking → fleeing → fled (null — gone until remount/reload)
   * No API call is made; the description is silently discarded.
   */
  function triggerFlee() {
    setOpen(false);   // slam the panel shut immediately
    setWarbachMood('screaming');

    const t1 = setTimeout(() => setWarbachMood('panicking'), SCREAM_MS);
    const t2 = setTimeout(() => setWarbachMood('fleeing'),   SCREAM_MS + PANIC_MS);
    const t3 = setTimeout(() => setWarbachMood('fled'),      SCREAM_MS + PANIC_MS + FLEE_MS);
    fleeTimers.current = [t1, t2, t3];
  }

  // Reset after successful submission when panel re-opens; always rotate the line.
  function openPanel() {
    setWarbachLine(WARBACH_LINES[Math.floor(Math.random() * WARBACH_LINES.length)]);
    if (submitted) {
      setSubmitted(false);
      setDescription('');
      setType('general');
      setError(null);
    }
    setOpen(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!description.trim() || submitting) return;

    // 🐉 Easter egg — intercept BEFORE the API call.
    // Undead keywords silently discard the report and scare Warbach away,
    // preventing spam submissions once users discover the trigger.
    if (mentionsUndead(description)) {
      triggerFlee();
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await apiFetch('/feedback', {
        method: 'POST',
        body: JSON.stringify({
          type,
          description: description.trim(),
          pageUrl:     window.location.href,
          pageTitle:   document.title,
          userAgent:   navigator.userAgent,
          viewport:    `${window.innerWidth}x${window.innerHeight}`,
          submittedByDisplayName: user?.displayName ?? user?.username,
          context: {
            pathname: location.pathname,
            search:   location.search,
          },
        }),
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(err?.message ?? 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // Don't show on login / callback pages (unauthenticated layout)
  if (!user) return null;

  // Warbach has fled — gone until the page reloads or the user navigates away
  if (warbachMood === 'fled') return null;

  // ── Animation class for the floating button ────────────────────────────────
  const buttonAnimCls =
    warbachMood === 'screaming'  ? 'animate-warbach-scream' :
    warbachMood === 'panicking'  ? 'animate-warbach-panic'  :
    warbachMood === 'fleeing'    ? 'animate-warbach-flee'   : '';

  // True during any animated phase — disables clicks and changes appearance
  const isAnimating = warbachMood === 'screaming'
                   || warbachMood === 'panicking'
                   || warbachMood === 'fleeing';

  return (
    <>
      {/* ── Backdrop (mobile) ── */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[1px] sm:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* ── Scream speech bubble ──────────────────────────────────────────────
          Only visible during the initial shake. It fades out before the
          running begins so it doesn't sit awkwardly in the corner while
          Warbach sprints around the bottom of the screen. */}
      {warbachMood === 'screaming' && (
        <div
          className="fixed bottom-[4.6rem] left-3 z-50
                     select-none pointer-events-none animate-fade-in"
        >
          <div className="relative bg-darkbg-card border-2 border-danger/70 rounded-xl
                          px-3 py-1.5 shadow-glow-sm">
            <p className="font-display text-danger text-sm font-bold tracking-widest whitespace-nowrap">
              !! AAAAAAH !! 💀
            </p>
            {/* Triangle pointing down toward Warbach */}
            <span
              className="absolute -bottom-[9px] left-6
                         border-l-[7px] border-r-[7px] border-t-[9px]
                         border-l-transparent border-r-transparent border-t-danger/70"
            />
          </div>
        </div>
      )}

      {/* ── Floating button ──
          Anchored bottom-LEFT at all breakpoints so it never overlaps
          right-aligned page actions (e.g. "Post Reply", the Upload slide-over).
          When overlap detection finds interactive content beneath the button
          the label text is hidden, shrinking the footprint to the avatar only. */}
      <button
        ref={buttonRef}
        type="button"
        onClick={isAnimating ? undefined : (open ? () => setOpen(false) : openPanel)}
        title={isAnimating ? undefined : 'Give feedback to Warbach'}
        aria-label={isAnimating ? undefined : 'Open feedback panel'}
        disabled={isAnimating}
        className={`fixed bottom-5 left-3 z-50 flex items-center gap-2
                    rounded-full shadow-glow-sm border
                    pr-3 pl-1 py-1
                    ${isAnimating
                      ? 'pointer-events-none border-danger/40 bg-darkbg-card'
                      : `transition-all duration-200 ${
                          isObscuring && !open
                            ? 'opacity-60 bg-darkbg-card border-darkbg-border/60 hover:opacity-100 hover:border-gold/50'
                            : open
                              ? 'bg-darkbg-secondary border-gold/60'
                              : 'bg-darkbg-card border-darkbg-border hover:border-gold/50 hover:shadow-glow'
                        }`
                    }
                    ${buttonAnimCls}`}
      >
        <WarbachAvatar size={36} glowing={open && !isAnimating} />
        {/* Label is hidden when we detect the button is sitting over interactive
            content — the avatar-only pill is much less likely to block anything. */}
        {!isObscuring && (
          <span className={`text-xs font-display tracking-wide transition-colors ${
            isAnimating
              ? 'text-danger'
              : open ? 'text-gold' : 'text-parchment-muted'
          }`}>
            {warbachMood === 'panicking' ? 'AAAAAH!' : isAnimating ? 'NOPE!' : 'Feedback'}
          </span>
        )}
      </button>

      {/* ── Slide-up panel ── */}
      {open && !isAnimating && (
        <div
          ref={panelRef}
          className="fixed bottom-20 left-3 z-50
                     w-[min(380px,calc(100vw-1.5rem))]
                     bg-darkbg-card border border-darkbg-border rounded-xl shadow-glow
                     animate-slide-up overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-start gap-3 px-4 py-3 bg-darkbg-tertiary border-b border-darkbg-border">
            <WarbachAvatar size={32} className="mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0 pt-0.5">
              <p className="font-display text-sm text-[#ae884c] tracking-wide mb-1">Warbach</p>
              <div className="relative bg-darkbg border border-[#ae884c]/30 rounded-xl
                              px-3 py-2 text-sm text-parchment/85 leading-relaxed">
                {warbachLine}
                {/* Tail pointing left toward Warbach */}
                <span
                  className="absolute left-[-7px] top-4
                             border-t-[6px] border-b-[6px] border-r-[7px]
                             border-t-transparent border-b-transparent border-r-[#ae884c]/30"
                />
              </div>
              <p className="text-parchment-muted/40 text-xs mt-1.5 pl-0.5">
                Kobold Scout &amp; Feedback Collector
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              title="Close"
              className="mt-0.5 p-1 rounded text-parchment-muted/40 hover:text-parchment-muted
                         hover:bg-darkbg transition-colors shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          {submitted ? (
            /* Success state */
            <div className="flex flex-col items-center gap-3 py-8 px-4 text-center">
              <CheckCircle2 className="w-10 h-10 text-success" />
              <p className="font-display text-parchment text-sm">Warbach is grateful!</p>
              <p className="text-parchment-muted/70 text-xs leading-relaxed">
                Your feedback has been recorded and will be reviewed by the developers.
                Every report helps make Darkfeld better.
              </p>
              <button
                type="button"
                onClick={() => { setSubmitted(false); setDescription(''); setType('general'); }}
                className="mt-2 btn-ghost text-xs"
              >
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-4 space-y-4">

              {/* Type selector */}
              <div className="grid grid-cols-3 gap-2">
                {FEEDBACK_TYPES.map(({ value, label, Icon, activeClass }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setType(value)}
                    className={`flex flex-col items-center gap-1 px-2 py-2 rounded-lg border text-xs
                                transition-all duration-150 ${
                      type === value
                        ? activeClass
                        : 'border-darkbg-border text-parchment-muted/60 hover:border-darkbg-border/60 hover:text-parchment-muted'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </div>

              {/* Type description */}
              <p className="text-xs text-parchment-muted/60 -mt-2">
                {FEEDBACK_TYPES.find((t) => t.value === type)?.description}
              </p>

              {/* Description */}
              <div>
                <label className="block text-xs text-parchment-muted mb-1">
                  Description <span className="text-danger/70">*</span>
                </label>
                <textarea
                  ref={textareaRef}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  placeholder={
                    type === 'bug'     ? "Describe what happened and what you expected…" :
                    type === 'feature' ? "Describe the feature or improvement you have in mind…" :
                                         "Share your thoughts with Warbach…"
                  }
                  className="w-full bg-darkbg border border-darkbg-border rounded px-3 py-2
                             text-parchment text-sm placeholder-parchment-muted/30
                             focus:outline-none focus:border-gold/60 resize-none
                             leading-relaxed"
                />
                <p className="text-right text-xs text-parchment-muted/40 mt-0.5">
                  {description.length}/2000
                </p>
              </div>

              {/* Context note */}
              <p className="text-xs text-parchment-muted/40 leading-relaxed">
                Your current page URL, browser info, and viewport size will be included
                automatically to help developers reproduce the issue.
              </p>

              {/* Error */}
              {error && (
                <p className="text-xs text-danger bg-danger/10 border border-danger/20 rounded px-3 py-2">
                  {error}
                </p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={!description.trim() || submitting}
                className="w-full btn-primary flex items-center justify-center gap-2 text-sm"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send to Warbach
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
}
